import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-License-Identifier: AGPL-3.0-only
import { Slot } from '@radix-ui/react-slot';
import { CircleAlert } from 'lucide-react';
import { createContext, useContext, useId } from 'react';
import { cn } from '../../lib/cn.js';
import { Label } from '../label/index.js';
const FormFieldContext = createContext(null);
/**
 * Read the enclosing `FormField` wiring (id / describedby / invalid /
 * required). Composite controls that cannot receive Slot-injected props on a
 * single root element (e.g. `Combobox` triggers) use this to self-wire.
 * Returns `null` outside a `FormField`.
 */
export function useFormField() {
    return useContext(FormFieldContext);
}
/**
 * FormField — label + control + helper/error caption
 * (research/design-system.md Tier 2). Accessibility wiring (`htmlFor`,
 * `aria-describedby`, `aria-invalid`, `aria-required`) is injected into the
 * child control via Slot and also exposed through `useFormField()` context.
 * RHF-agnostic: pass `error` from any form library.
 */
export function FormField({ label, required = false, helper, error, tag, controlId, className, children, ...props }) {
    const generatedId = useId();
    const id = controlId ?? generatedId;
    const invalid = error !== undefined && error !== null && error !== false;
    const caption = invalid ? error : helper;
    const descriptionId = caption !== undefined && caption !== null ? `${id}-caption` : undefined;
    return (_jsx(FormFieldContext.Provider, { value: { id, descriptionId, invalid, required }, children: _jsxs("div", { className: cn('flex w-full flex-col gap-1.5', className), ...props, children: [_jsxs("div", { className: "flex items-center gap-1.5", children: [_jsxs(Label, { htmlFor: id, children: [label, required ? (_jsx("span", { "aria-hidden": "true", className: "ms-0.5 text-danger", children: "*" })) : null] }), tag ? _jsx("span", { className: "ms-auto flex items-center", children: tag }) : null] }), _jsx(Slot, { id: id, ...(descriptionId ? { 'aria-describedby': descriptionId } : {}), ...(invalid ? { 'aria-invalid': true } : {}), ...(required ? { 'aria-required': true } : {}), children: children }), descriptionId ? (_jsxs("p", { id: descriptionId, className: cn('text-[11.5px] leading-4', invalid ? 'flex items-center gap-1 font-medium text-danger' : 'text-fg-muted'), children: [invalid ? _jsx(CircleAlert, { "aria-hidden": "true", className: "size-3.5 shrink-0" }) : null, caption] })) : null] }) }));
}
//# sourceMappingURL=FormField.js.map